import React, { Fragment, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Plus } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { useGetCompaniesQuery } from "@redux/features/companies/companiesApi";
import { switchCompany } from "@redux/features/auth/authSlice";
import CompanyCreationModal from "@components/Dashboard/Sidebar/SidebarCompanySwitcher/CompanyCreationModal";
import { cn, declOfNum, extractInitials, toastError } from "@helpers/utils";
import BaseButton from "@reusables/BaseButton";
import BaseMaterialIconButton from "@reusables/BaseMaterialIconButton";
import { ReactComponent as WireframeLogo } from "@assets/images/logos/suppli_logo_wireframe.svg";
import { Divider } from "@mui/material";

const imageDomainURLPrefix = import.meta.env.VITE_API_URL;

const FullscreenCompanySwitcher = () => {
    const { t } = useTranslation("", { keyPrefix: "dashboard.companySwitcher.fullscreenSelector" });
    const dispatch = useAppDispatch();
    const history = useHistory();

    const {
        data: companiesOptions,
        isLoading: companiesOptionsLoading
    } = useGetCompaniesQuery(undefined, { refetchOnMountOrArgChange: true });

    const user = useAppSelector(state => state.auth.user);
    const activeCompany = useAppSelector(state => state.auth.user?.company);

    const [companyIdSwitchLoading, setCompanyIdSwitchLoading] = useState<number | undefined>();
    const [isCreationModalOpen, setIsCreationModalOpen] = useState(false);

    const handleSelectedCompanyChange = async (company: any) => {
        if (!company || companyIdSwitchLoading) return;

        try {
            setCompanyIdSwitchLoading(company.id);

            await dispatch(switchCompany(company.id));

            history.push("/dashboard");
        } catch (e) {
            toastError(e as any);
        } finally {
            setCompanyIdSwitchLoading(undefined);
        }
    };

    const handleCreationModalOpen = () => setIsCreationModalOpen(true);
    const handleCreationModalClose = () => setIsCreationModalOpen(false);

    /**
     *  Redirect to main dashboard if the user has selected a company
     */
    useEffect(() => {
        if (activeCompany != null) {
            history.push("/dashboard/main");
        }
    }, [activeCompany]);

    return (
        <div className="min-h-screen flex flex-row bg-gray-[#FBFEFF]">
            {/* Left side: Content */}
            <div className="flex flex-col grow justify-center w-full px-12 min-h-screen">
                <h1 className="text-3xl font-semibold mb-2 text-accent">{t("heading")}</h1>
                <p className="mb-8 text-accent">
                    <Trans
                        i18nKey="subheading"
                        t={t}
                        values={{ email: user?.email }}
                        components={{ b: <span className="font-semibold" /> }}
                    />
                </p>

                <div
                    className="mb-6 max-w-[600px] bg-[#ECEDFE] rounded-[16px] p-[4px] space-y-2">
                    {companiesOptionsLoading ? (
                        // TODO: Replace with a better loader
                        <div className="p-8 text-center">{t("loading")}</div>
                    ) : companiesOptions && companiesOptions.length > 0 ? (
                        companiesOptions.map((company, index) => {
                            const isSwitchLoading = companyIdSwitchLoading === company.id;
                            return (
                                <Fragment key={company.id}>
                                    <div className="flex items-center justify-between px-6 py-[16px]">
                                        <div className="flex items-center space-x-4">
                                            {company.logo ? (
                                                <div
                                                    style={{
                                                        width: "40px",
                                                        height: "40px",
                                                        backgroundImage: `url(${imageDomainURLPrefix + company.logo})`,
                                                        backgroundRepeat: "no-repeat",
                                                        backgroundPosition: "center",
                                                        backgroundSize: "cover",
                                                        borderRadius: "8px",
                                                        flexShrink: 0
                                                    }}
                                                    aria-label={`${company.name} logo`}
                                                />
                                            ) : (
                                                <div
                                                    className="flex items-center justify-center w-[48px] h-[48px] rounded-md flex-shrink-0 bg-[#FDDDFC] text-[#D847D3] text-[20px]">
                                                    {extractInitials(company.name)}
                                                </div>
                                            )}
                                            <div>
                                                <div className="font-[600] text-[#5A5587]">{company.name}</div>
                                                <span className="text-sm text-[#686868]">
                                                {company.members} {declOfNum(company.members, [t("companyCard.members.0"), t("companyCard.members.1"), t("companyCard.members.2")])}
                                            </span>
                                            </div>
                                        </div>
                                        <BaseButton
                                            text={t("companyCard.join")}
                                            size="xs"
                                            onClick={() => handleSelectedCompanyChange(company)}
                                            loading={isSwitchLoading}
                                            disabled={companyIdSwitchLoading !== undefined}
                                            className="rounded-[12px] w-[100px] hover:opacity-80 transition-none"
                                        />
                                    </div>
                                    {
                                        index != companiesOptions.length - 1 &&
                                        <div className={"px-[80px]"}>
                                            <Divider className={"bg-[#D9DAFD]"} />
                                        </div>
                                    }
                                </Fragment>
                            );
                        })
                    ) : (
                        <div className="p-8 text-center text-gray-500">
                            {t("noResults")}
                        </div>
                    )}
                </div>
                <div
                    className={cn(
                        "flex items-center gap-2 cursor-pointer mt-2 text-[#5A5587] group",
                        companyIdSwitchLoading !== undefined && "opacity-80 cursor-not-allowed"
                    )}
                    onClick={companyIdSwitchLoading === undefined ? handleCreationModalOpen : undefined}
                >
                    <BaseMaterialIconButton
                        icon={<Plus size={20} className="text-[#686868]" />}
                        size="medium"
                        disabled={companyIdSwitchLoading !== undefined}
                        style="light"
                        className="!border-none !bg-[#F4F4F4]"
                    />
                    <span className="font-thin group-hover:opacity-80">{t("buttons.create")}</span>
                </div>
                <CompanyCreationModal
                    isOpen={isCreationModalOpen}
                    onClose={handleCreationModalClose}
                    onCreateCompany={async (company) => {
                        await handleSelectedCompanyChange(company);
                        handleCreationModalClose();
                    }}
                />
            </div>

            {/* Right side: Purple rectangle for logo */}
            <div
                className="hidden md:flex min-h-screen items-center justify-center p-4 relative overflow-hidden w-[300px] lg:w-[600px] flex-shrink-0 textured-panel">
                <WireframeLogo className="relative z-10" />

                {/* Blurred Circle 1 */}
                <div
                    className="absolute w-[800px] h-[800px] opacity-70 bg-[#B1B2E1] rounded-full pointer-events-none"
                    style={{
                        bottom: "0",
                        right: "0",
                        filter: "blur(120px)",
                        transform: "translate3d(50%, 50%, 0)"
                    }}
                />
                {/* Blurred Circle 2 - Vertically Centered on the Right Edge */}
                <div
                    className="absolute w-[700px] h-[700px] opacity-70 bg-[#B1B2E1] rounded-full pointer-events-none" // Removed blur-3xl
                    style={{
                        top: "50%",
                        transform: "translate3d(80%, -50%, 0)",
                        filter: "blur(120px)"
                    }}
                />
            </div>
        </div>
    );
};

export default FullscreenCompanySwitcher;